// Street Interview Ad's script as live's form reads it (spec G14-street-interview-ad, G14 PR 17;
// live's `street-interview-ad-form/core/director/script.ts` and `core/utils.ts`): the 17 beats,
// the blank script "Write the 17 lines yourself" fills, the meta line, and what keeps the script
// from approval. The server checks the same on Approve (`street_interview_draft.lines_issue`).
import type { StreetInterviewAdInputsCrowd, StreetScript, StreetSpeaker } from "../../../api/generated/model";
import { countWords } from "./podcast-script";

/** Live's words (`core/constants.ts`, `library-view/constants.ts`). */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const GENERATE_SCRIPT = "Generate or write the script";
export const ACTOR_PENDING = "Wait for the AI Actor to finish";
export const CAST_PENDING = "Wait for the location and the five people";
export const STILLS_PENDING = "Wait for all keyframes";
export const SCRIPT_SHAPE = "The script needs its 17 lines in order — generate it again";
export const EMPTY_LINE = "Every line needs its text";
export const SCRIPT_PROBLEMS =
  "Some product facts are not quoted from the product text — check the lines before approving.";
export const UNREADABLE_PACK =
  "The pack was hard to read — type the product name or its text and generate again.";

interface Beat {
  beat: string;
  speaker: StreetSpeaker;
  maxWords: number;
}

/** Live's 17 beats in speaking order, with who says each and its most words (`BEATS`). */
export const BEATS: readonly Beat[] = [
  { beat: "question", speaker: "INTERVIEWER", maxWords: 12 },
  { beat: "goofy_answer", speaker: "P1", maxWords: 14 },
  { beat: "habit_answer", speaker: "P2", maxWords: 14 },
  { beat: "skeptic_answer", speaker: "P3", maxWords: 8 },
  { beat: "pain_answer", speaker: "P4", maxWords: 18 },
  { beat: "handover", speaker: "INTERVIEWER", maxWords: 4 },
  { beat: "sensory_reaction", speaker: "P3", maxWords: 10 },
  { beat: "product_name", speaker: "INTERVIEWER", maxWords: 14 },
  { beat: "reads_pack", speaker: "P2", maxWords: 18 },
  { beat: "detail_question", speaker: "P1", maxWords: 8 },
  { beat: "detail_answer", speaker: "INTERVIEWER", maxWords: 10 },
  { beat: "detail_reaction", speaker: "P1", maxWords: 12 },
  { beat: "whats_different", speaker: "P3", maxWords: 8 },
  { beat: "mechanism", speaker: "INTERVIEWER", maxWords: 22 },
  { beat: "realization", speaker: "P4", maxWords: 10 },
  { beat: "converted", speaker: "P3", maxWords: 14 },
  { beat: "sign_off", speaker: "INTERVIEWER", maxWords: 8 },
];
/** Live's `CLIP_COUNT` and `WORD_TARGET`. */
const CLIP_COUNT = 11;
const WORD_TARGET = { min: 130, max: 160 };
/** Live's end card button when the form gives none (`toBlankScript`). */
const DEFAULT_CTA = "Shop now";

/** The most words line `index` has (live's `getMaxWords`). */
export function maxWordsAt(index: number): number {
  return BEATS[index]?.maxWords ?? 0;
}

function scriptWords(lines: StreetScript["lines"]): number {
  return lines.reduce((total, line) => total + countWords(line.text), 0);
}

/** Live's pace (`estimateDurationSeconds`): three words a second, 0.6 s a clip, two seconds of
 * end card, to a tenth. */
function estimatedSeconds(lines: StreetScript["lines"]): number {
  return Math.round((scriptWords(lines) / 3 + 0.6 * CLIP_COUNT + 2) * 10) / 10;
}

/** Live's meta line (`toScriptMeta`): "17 lines · 0 words (130–160) · ≈ 8.6 s". */
export function scriptMeta(lines: StreetScript["lines"]): string {
  const { min, max } = WORD_TARGET;
  return `${lines.length} lines · ${scriptWords(lines)} words (${min}–${max}) · ≈ ${estimatedSeconds(lines)} s`;
}

/** "Write the 17 lines yourself" (live's `toBlankScript`): a blank line a beat, the question and
 * the crowd as picked (Auto is mixed), the end card the product's name and the button the CTA's
 * (live's "Shop now" without one). */
export function blankScript(options: {
  question: string;
  crowd: StreetInterviewAdInputsCrowd;
  productName: string;
  cta: string;
}): StreetScript {
  const { question, crowd, productName, cta } = options;
  return {
    question,
    crowd: crowd === "auto" ? "mixed" : crowd,
    topic_noun: "",
    pain: "",
    mechanism: "",
    claims: [],
    lines: BEATS.map(({ beat, speaker }, index) => ({
      id: index + 1,
      beat,
      speaker,
      text: "",
      claim_ids: [],
    })),
    pronunciations: [],
    word_count: 0,
    estimated_duration_s: 0,
    lower_third: "",
    end_card: { headline: productName, cta_button: cta || DEFAULT_CTA, offer_line: "" },
  };
}

/** What keeps the lines from approval (live's `toLinesIssue`, and ours: each line its number,
 * beat and speaker, G14 Street Interview-3), or null. */
export function scriptIssue(script: StreetScript | null): string | null {
  if (script === null) return GENERATE_SCRIPT;
  const inOrder =
    script.lines.length === BEATS.length &&
    script.lines.every(
      (line, at) => line.id === at + 1 && line.beat === BEATS[at]?.beat && line.speaker === BEATS[at]?.speaker,
    );
  if (!inOrder) return SCRIPT_SHAPE;
  return script.lines.some((line) => line.text.trim() === "") ? EMPTY_LINE : null;
}

/** A script's lines as `sameLineTexts` compares them: any template's. */
interface Lines {
  lines: readonly { text: string }[];
}

/** Whether two scripts have the same lines, word for word (live's shared `hasSameLineTexts`). */
export function sameLineTexts(script: Lines, other: Lines | null): boolean {
  return (
    other !== null &&
    script.lines.length === other.lines.length &&
    script.lines.every((line, at) => line.text === other.lines[at]?.text)
  );
}
